// EmojiPicker.tsx
import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import { Search } from "lucide-react";
import { registerChatEscapeLayer } from "../../../utilities/chat/chatEscapeLayers";

interface EmojiPickerProps {
  onSelect: (emoji: string) => void;
  compact?: boolean;
  autoFocusSearch?: boolean;
  onDismiss?: () => void;
}

const QUICK_EMOJIS = ["👍", "❤️", "😂", "🎉", "😮", "😢", "🙏", "🔥"];
const EMOJI_PICKER_OPEN_EVENT = "chat-emoji-picker-open";

export const searchEmojis = (query: string, limit = Number.POSITIVE_INFINITY): EmojiItem[] => {
  const normalized = query.trim().toLowerCase().replace(/^:/, "");
  const uniqueItems = Array.from(
    new Map(emojiData.map((item) => [item.emoji, item])).values()
  );

  if (!normalized) {
    const quickSet = new Set(QUICK_EMOJIS);
    return [
      ...uniqueItems.filter((item) => quickSet.has(item.emoji)),
      ...uniqueItems.filter((item) => !quickSet.has(item.emoji)),
    ].slice(0, limit);
  }

  return uniqueItems
    .map((item) => {
      const keywords = item.keywords.map((keyword) => keyword.toLowerCase());
      const exactIndex = keywords.findIndex((keyword) => keyword === normalized);
      const prefixIndex = keywords.findIndex((keyword) => keyword.startsWith(normalized));
      const containsIndex = keywords.findIndex((keyword) => keyword.includes(normalized));
      const score = exactIndex >= 0 ? 0 : prefixIndex >= 0 ? 1 : containsIndex >= 0 ? 2 : 99;
      return { item, score };
    })
    .filter(({ score }) => score < 99)
    .sort((a, b) => a.score - b.score || a.item.keywords[0].localeCompare(b.item.keywords[0]))
    .slice(0, limit)
    .map(({ item }) => item);
};

const EmojiPicker: React.FC<EmojiPickerProps> = ({ onSelect, compact = false, autoFocusSearch = false, onDismiss }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const reactId = useId();
  const pickerIdRef = useRef(`chat-emoji-picker-${reactId.replace(/:/g, "")}`);
  const pickerInstanceIdRef = useRef(`${pickerIdRef.current}-${Math.random().toString(36).slice(2)}`);
  const pickerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const optionRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const onDismissRef = useRef(onDismiss);

  // The scrollable button picker exposes the entire shared catalog. Colon
  // autocomplete applies its own smaller result limit after searching it.
  const filteredEmojis = useMemo(() => searchEmojis(searchTerm), [searchTerm]);

  useEffect(() => {
    onDismissRef.current = onDismiss;
  }, [onDismiss]);

  useEffect(() => {
    const handleAnotherPickerOpened = (event: Event) => {
      const sourceId = (event as CustomEvent<{ id?: string }>).detail?.id;
      if (sourceId !== pickerInstanceIdRef.current) onDismissRef.current?.();
    };

    window.addEventListener(EMOJI_PICKER_OPEN_EVENT, handleAnotherPickerOpened);
    window.dispatchEvent(new CustomEvent(EMOJI_PICKER_OPEN_EVENT, {
      detail: { id: pickerInstanceIdRef.current },
    }));
    return () => window.removeEventListener(EMOJI_PICKER_OPEN_EVENT, handleAnotherPickerOpened);
  }, []);

  useEffect(() => {
    if (!onDismiss) return;
    return registerChatEscapeLayer(onDismiss);
  }, [onDismiss]);

  useEffect(() => {
    if (!autoFocusSearch) return;
    const frame = window.requestAnimationFrame(() => searchInputRef.current?.focus());
    return () => window.cancelAnimationFrame(frame);
  }, [autoFocusSearch]);

  useEffect(() => {
    setActiveIndex(0);
  }, [searchTerm]);

  useEffect(() => {
    if (!filteredEmojis.length) return;
    if (activeIndex >= filteredEmojis.length) setActiveIndex(0);
    else optionRefs.current[activeIndex]?.scrollIntoView?.({ block: "nearest" });
  }, [activeIndex, filteredEmojis.length]);

  useEffect(() => {
    if (!onDismiss) return;
    const handleOutsidePointer = (event: PointerEvent) => {
      const target = event.target as HTMLElement | null;
      if (!target || pickerRef.current?.contains(target) || target.closest("[data-emoji-picker-trigger]")) return;
      onDismissRef.current?.();
    };
    document.addEventListener("pointerdown", handleOutsidePointer);
    return () => document.removeEventListener("pointerdown", handleOutsidePointer);
  }, [onDismiss]);

  const selectActiveEmoji = () => {
    const item = filteredEmojis[activeIndex];
    if (item) onSelect(item.emoji);
  };

  const handleSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (!filteredEmojis.length) return;
    const columns = 8;
    let nextIndex = activeIndex;

    if (event.key === "ArrowRight") nextIndex = (activeIndex + 1) % filteredEmojis.length;
    else if (event.key === "ArrowLeft") nextIndex = (activeIndex - 1 + filteredEmojis.length) % filteredEmojis.length;
    else if (event.key === "ArrowDown") nextIndex = Math.min(filteredEmojis.length - 1, activeIndex + columns);
    else if (event.key === "ArrowUp") nextIndex = Math.max(0, activeIndex - columns);
    else if (event.key === "Enter" || (event.key === "Tab" && !event.shiftKey)) {
      event.preventDefault();
      selectActiveEmoji();
      return;
    } else return;

    event.preventDefault();
    setActiveIndex(nextIndex);
  };

  return (
    <div ref={pickerRef} className={`chat-emoji-picker emoji_picker_card flex max-w-[calc(100vw-32px)] flex-col gap-2 overflow-x-hidden rounded-xl bg-white p-2 shadow-xl dark:bg-slate-800 ${compact ? "!w-72" : "!w-80"}`}>
      <label className="chat-emoji-search flex items-center gap-2 rounded-lg bg-slate-100 px-2.5 transition">
        <Search size={14} className="shrink-0 text-slate-400" />
        <input
          ref={searchInputRef}
          type="text"
          placeholder="Search emojis"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyDown={handleSearchKeyDown}
          className="min-w-0 flex-1 border-0 bg-transparent px-0 py-2 text-xs text-slate-700 outline-none ring-0 shadow-none"
          autoFocus={autoFocusSearch}
          aria-label="Search emojis"
          aria-activedescendant={filteredEmojis[activeIndex] ? `${pickerIdRef.current}-option-${activeIndex}` : undefined}
        />
      </label>
      <div className={`grid w-full grid-cols-8 gap-0.5 overflow-x-hidden overflow-y-auto ${compact ? "max-h-48" : "max-h-64"}`}>
        {filteredEmojis.map((item, index) => (
          <button
            key={item.emoji}
            id={`${pickerIdRef.current}-option-${index}`}
            ref={(element) => { optionRefs.current[index] = element; }}
            type="button"
            className={`chat-emoji-option flex h-8 w-8 cursor-pointer items-center justify-center rounded-lg text-lg transition hover:scale-110 hover:bg-slate-100 focus:outline-none dark:hover:bg-slate-700 ${index === activeIndex ? "chat-emoji-option-selected bg-purple-50 text-[#6847A3] ring-2 ring-inset ring-[#7E57C2]/35 dark:bg-purple-400/15 dark:text-purple-200" : ""}`}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => onSelect(item.emoji)}
            onMouseEnter={() => setActiveIndex(index)}
            title={`:${item.keywords[0]}:`}
            aria-label={item.keywords.join(", ")}
            aria-current={index === activeIndex ? "true" : undefined}
          >
            {item.emoji}
          </button>
        ))}
      </div>
      {filteredEmojis.length === 0 && (
        <div className="px-2 py-4 text-center text-xs text-slate-400">No matching emojis</div>
      )}
      <div className="chat-emoji-tip px-1 text-[10px] text-slate-400">Tip: type <span className="font-semibold text-slate-500">:</span> in a message to search from the keyboard.</div>
    </div>
  );
};

export default EmojiPicker;

// emojiData.ts
export interface EmojiItem {
  emoji: string;
  keywords: string[];
}

/**
 * Shared emoji catalog for the button picker, colon autocomplete, and reaction
 * picker. Add one object here to make an emoji available everywhere. The first
 * keyword is its displayed shortcode; include common aliases after it.
 *
 * Example: { emoji: "🪩", keywords: ["mirror_ball", "disco", "dance", "party"] }
 */
export const emojiData: EmojiItem[] = [
  // Happy and Smiling Emojis
  { emoji: "😀", keywords: ["grinning", "face", "smile", "happy", "joy"] },
  { emoji: "😃", keywords: ["grinning", "face", "big", "eyes", "happy"] },
  { emoji: "😄", keywords: ["grinning", "face", "smile", "eyes", "happy"] },
  { emoji: "😁", keywords: ["beaming", "face", "smile", "happy"] },
  {
    emoji: "😆",
    keywords: ["grinning", "squinting", "face", "laugh", "happy"],
  },
  { emoji: "😅", keywords: ["grinning", "face", "sweat", "nervous"] },
  { emoji: "😂", keywords: ["face", "tears", "joy", "laugh", "happy"] },
  { emoji: "🤣", keywords: ["rolling", "floor", "laughing", "rofl"] },
  { emoji: "😊", keywords: ["smiling", "face", "smile", "blush", "happy"] },
  { emoji: "😇", keywords: ["smiling", "face", "halo", "angel", "innocent"] },
  { emoji: "🙂", keywords: ["slightly", "smiling", "face", "smile"] },
  { emoji: "😉", keywords: ["winking", "face", "wink", "playful"] },
  { emoji: "😍", keywords: ["smiling", "face", "heart", "eyes", "love"] },
  { emoji: "🥰", keywords: ["smiling", "face", "hearts", "love", "affection"] },
  { emoji: "😘", keywords: ["face", "blowing", "kiss", "love"] },

  // Love and Affection Emojis
  { emoji: "😗", keywords: ["kissing", "face", "affection"] },
  { emoji: "😙", keywords: ["kissing", "face", "smile"] },
  { emoji: "😚", keywords: ["kissing", "face", "closed", "eyes"] },
  { emoji: "❤️", keywords: ["heart", "love", "affection", "romance"] },
  { emoji: "💋", keywords: ["kiss", "lipstick", "love", "affection"] },

  // Playful and Silly Emojis
  { emoji: "😋", keywords: ["face", "savoring", "food", "yum"] },
  { emoji: "😛", keywords: ["face", "tongue", "playful", "silly"] },
  { emoji: "😜", keywords: ["winking", "face", "tongue", "silly"] },
  { emoji: "🤪", keywords: ["zany", "face", "crazy", "silly"] },
  { emoji: "😝", keywords: ["squinting", "face", "tongue", "silly"] },
  { emoji: "🤑", keywords: ["money", "mouth", "face", "rich"] },

  // Gestures and Reactions
  { emoji: "🤗", keywords: ["hugging", "face", "hug", "smile"] },
  { emoji: "🤭", keywords: ["face", "hand", "mouth", "oops"] },
  { emoji: "🤫", keywords: ["shushing", "face", "quiet", "secret"] },
  { emoji: "🤔", keywords: ["thinking", "face", "thoughtful", "hmm"] },
  { emoji: "🤐", keywords: ["zipper", "mouth", "face", "secret"] },
  { emoji: "🤨", keywords: ["face", "raised", "eyebrow", "skeptical"] },
  { emoji: "😐", keywords: ["neutral", "face", "meh", "indifferent"] },
  { emoji: "😑", keywords: ["expressionless", "face", "blank"] },

  // Sad and Crying Emojis
  { emoji: "😔", keywords: ["pensive", "face", "sad", "disappointed"] },
  { emoji: "😞", keywords: ["disappointed", "face", "sad", "down"] },
  { emoji: "😟", keywords: ["worried", "face", "concerned", "sad"] },
  { emoji: "😢", keywords: ["crying", "face", "sad", "tear"] },
  { emoji: "😭", keywords: ["loudly", "crying", "face", "sad"] },
  { emoji: "😩", keywords: ["weary", "face", "tired", "frustrated"] },
  { emoji: "😫", keywords: ["tired", "face", "exhausted"] },
  { emoji: "🥺", keywords: ["pleading", "face", "puppy", "eyes"] },

  // Angry and Annoyed Emojis
  { emoji: "😠", keywords: ["angry", "face", "mad", "irritated"] },
  { emoji: "😡", keywords: ["pouting", "face", "angry", "mad"] },
  { emoji: "🤬", keywords: ["cursing", "face", "angry", "mad", "swear"] },
  { emoji: "😤", keywords: ["face", "triumph", "proud", "snort"] },

  // Surprise and Shock Emojis
  { emoji: "😯", keywords: ["hushed", "face", "surprised", "silent"] },
  { emoji: "😲", keywords: ["astonished", "face", "shocked", "wow"] },
  { emoji: "😱", keywords: ["screaming", "face", "fear", "horror"] },
  { emoji: "🤯", keywords: ["mind", "blown", "exploding", "head"] },

  // Sleepy and Tired Emojis
  { emoji: "😴", keywords: ["sleeping", "face", "zzz", "tired"] },
  { emoji: "😪", keywords: ["sleepy", "face", "tired", "zzz"] },
  { emoji: "😌", keywords: ["relieved", "face", "relaxed", "calm"] },

  // Confused and Disbelief Emojis
  { emoji: "😕", keywords: ["confused", "face", "puzzled", "uncertain"] },
  { emoji: "🙁", keywords: ["slightly", "frowning", "face", "sad"] },
  { emoji: "🤥", keywords: ["lying", "face", "liar", "nose"] },

  // Celebratory and Party Emojis
  { emoji: "🥳", keywords: ["partying", "face", "celebration", "fun"] },
  { emoji: "🎉", keywords: ["party", "celebration", "confetti"] },
  { emoji: "🎊", keywords: ["confetti", "ball", "party", "celebration"] },

  // Animal Emojis
  { emoji: "🐶", keywords: ["dog", "puppy", "animal"] },
  { emoji: "🐱", keywords: ["cat", "animal", "cute"] },
  { emoji: "🐭", keywords: ["mouse", "animal", "cute"] },
  { emoji: "🐹", keywords: ["hamster", "animal", "cute"] },
  { emoji: "🦊", keywords: ["fox", "animal", "clever"] },
  { emoji: "🐼", keywords: ["panda", "bear", "animal", "cute"] },
  { emoji: "🐻", keywords: ["bear", "animal"] },

  // Food Emojis
  { emoji: "🍏", keywords: ["green", "apple", "fruit"] },
  { emoji: "🍔", keywords: ["burger", "food", "fast"] },
  { emoji: "🍕", keywords: ["pizza", "food", "fast"] },
  { emoji: "🍣", keywords: ["sushi", "food", "japanese"] },
  { emoji: "🍦", keywords: ["ice", "cream", "dessert", "food"] },
  { emoji: "🍰", keywords: ["cake", "dessert", "food", "sweet"] },

  // Weather and Nature Emojis
  { emoji: "🌞", keywords: ["sun", "happy", "weather", "bright"] },
  { emoji: "🌧️", keywords: ["rain", "weather", "storm", "cloud"] },
  { emoji: "⛈️", keywords: ["thunderstorm", "weather", "storm"] },
  { emoji: "🌈", keywords: ["rainbow", "weather", "happy"] },

  // Objects and Miscellaneous
  { emoji: "💡", keywords: ["light", "bulb", "idea", "bright"] },
  { emoji: "🔥", keywords: ["fire", "lit", "burn", "hot"] },
  { emoji: "💀", keywords: ["skull", "death", "danger"] },
  { emoji: "🎁", keywords: ["gift", "present", "birthday"] },
  { emoji: "📱", keywords: ["phone", "mobile", "device", "communication"] },

  // Additional Love and Affection Emojis
  { emoji: "💖", keywords: ["sparkling", "heart", "love"] },
  { emoji: "💞", keywords: ["revolving", "hearts", "love"] },
  { emoji: "💓", keywords: ["heartbeat", "love", "affection"] },

  // Additional Playful and Silly Emojis
  { emoji: "😮", keywords: ["open", "mouth", "shock", "surprised"] },

  // Additional Angry and Annoyed Emojis
  { emoji: "😮‍💨", keywords: ["exhale", "relief", "disappointed"] },

  // Additional Celebratory and Party Emojis
  { emoji: "🎂", keywords: ["cake", "birthday", "celebration"] },

  // Additional Animal Emojis
  { emoji: "🦁", keywords: ["lion", "roar", "animal"] },

  // Additional Food Emojis
  { emoji: "🍎", keywords: ["apple", "red", "fruit"] },
  { emoji: "🎂", keywords: ["cake", "birthday", "celebration"] },

  // Additional Weather and Nature Emojis
  { emoji: "☀️", keywords: ["sun", "sunny", "hot"] },

  // Additional Objects and Miscellaneous
  { emoji: "🖥️", keywords: ["computer", "pc", "desktop"] },
  { emoji: "🕶️", keywords: ["sunglasses", "cool", "style"] },
  { emoji: "🚀", keywords: ["rocket", "space", "launch", "fast"] },

  // Additional Love and Affection Emojis
  { emoji: "💗", keywords: ["growing", "heart", "love", "affection"] },
  { emoji: "💘", keywords: ["heart", "arrow", "love", "crush"] },
  { emoji: "💝", keywords: ["gift", "heart", "love", "present"] },
  { emoji: "💟", keywords: ["heart", "decorative", "symbol"] },

  // Additional Playful and Silly Emojis
  { emoji: "🫣", keywords: ["peeking", "eye", "shy", "curious"] },
  { emoji: "🫢", keywords: ["shocked", "surprised", "oops"] },

  // Additional Angry and Annoyed Emojis
  { emoji: "😒", keywords: ["unimpressed", "side-eye", "annoyed"] },

  // Additional Celebratory and Party Emojis
  { emoji: "🥂", keywords: ["cheers", "champagne", "celebration"] },
  { emoji: "🍾", keywords: ["bottle", "champagne", "celebrate"] },
  { emoji: "🎈", keywords: ["balloon", "party", "celebration"] },

  // Additional Animal Emojis
  { emoji: "🐵", keywords: ["monkey", "animal", "cute"] },
  { emoji: "🐸", keywords: ["frog", "animal", "ribbit"] },
  { emoji: "🦄", keywords: ["unicorn", "magic", "fantasy"] },
  { emoji: "🐢", keywords: ["turtle", "slow", "animal"] },
  { emoji: "🦖", keywords: ["t-rex", "dinosaur", "extinct"] },
  { emoji: "🦉", keywords: ["owl", "wise", "bird"] },

  // Additional Food Emojis
  { emoji: "🥑", keywords: ["avocado", "fruit", "green", "healthy"] },
  { emoji: "🍩", keywords: ["donut", "dessert", "sweet"] },
  { emoji: "🍉", keywords: ["watermelon", "fruit", "summer"] },
  { emoji: "🥦", keywords: ["broccoli", "vegetable", "healthy"] },
  { emoji: "🍩", keywords: ["donut", "dessert", "sweet"] },
  { emoji: "🍿", keywords: ["popcorn", "movie", "snack"] },

  // Additional Weather and Nature Emojis
  { emoji: "🌤️", keywords: ["partly sunny", "cloud", "weather"] },
  { emoji: "🌪️", keywords: ["tornado", "storm", "windy"] },
  { emoji: "❄️", keywords: ["snowflake", "cold", "winter"] },
  { emoji: "🔥", keywords: ["fire", "hot", "lit"] },
  { emoji: "🌊", keywords: ["wave", "ocean", "sea"] },
  { emoji: "🪵", keywords: ["wood", "log", "nature"] },

  // Additional Objects and Miscellaneous
  { emoji: "💡", keywords: ["idea", "lightbulb", "bright"] },
  { emoji: "🎮", keywords: ["video game", "controller", "fun"] },
  { emoji: "💰", keywords: ["money", "bag", "rich"] },
  { emoji: "🔑", keywords: ["key", "lock", "security"] },
  { emoji: "🧸", keywords: ["teddy bear", "plush", "toy"] },
  { emoji: "🎯", keywords: ["target", "bullseye", "goal"] },

  // Additional Activity and Sports Emojis
  { emoji: "⚽", keywords: ["soccer", "football", "sports"] },
  { emoji: "🏀", keywords: ["basketball", "sports", "hoops"] },
  { emoji: "🏈", keywords: ["football", "sports", "game"] },
  { emoji: "🎳", keywords: ["bowling", "game", "fun"] },
  { emoji: "⛷️", keywords: ["skiing", "snow", "sports"] },
  { emoji: "🎭", keywords: ["drama", "theater", "mask"] },
  { emoji: "🏆", keywords: ["trophy", "champion", "win"] },

  // Additional Travel and Transportation Emojis
  { emoji: "🚗", keywords: ["car", "automobile", "drive"] },
  { emoji: "✈️", keywords: ["airplane", "flight", "travel"] },
  { emoji: "🚢", keywords: ["ship", "boat", "cruise"] },
  { emoji: "🚲", keywords: ["bicycle", "bike", "ride"] },

  // Additional Flags
  { emoji: "🇺🇸", keywords: ["USA", "United States", "flag"] },
  { emoji: "🇨🇦", keywords: ["Canada", "flag", "maple"] },
  { emoji: "🇬🇧", keywords: ["UK", "United Kingdom", "flag"] },
  { emoji: "🇦🇺", keywords: ["Australia", "flag", "kangaroo"] },

  // Expanded emoji catalog
  { emoji: "🙃", keywords: ["upside down", "sarcasm", "silly"] },
  { emoji: "😎", keywords: ["sunglasses", "cool"] },
  { emoji: "🤩", keywords: ["star struck", "wow", "amazing"] },
  { emoji: "😏", keywords: ["smirk", "confident"] },
  { emoji: "😳", keywords: ["flushed", "embarrassed", "shocked"] },
  { emoji: "🥵", keywords: ["hot", "sweating"] },
  { emoji: "🥶", keywords: ["cold", "freezing"] },
  { emoji: "😨", keywords: ["fearful", "scared"] },
  { emoji: "😰", keywords: ["anxious", "nervous"] },
  { emoji: "😬", keywords: ["grimace", "awkward"] },
  { emoji: "🙄", keywords: ["eye roll", "annoyed"] },
  { emoji: "🥱", keywords: ["yawn", "tired"] },
  { emoji: "🤤", keywords: ["drooling", "hungry"] },
  { emoji: "😵", keywords: ["dizzy", "knocked out"] },
  { emoji: "😵‍💫", keywords: ["spiral eyes", "dizzy", "confused"] },
  { emoji: "🫠", keywords: ["melting", "awkward", "hot"] },
  { emoji: "🫡", keywords: ["salute", "respect"] },
  { emoji: "👻", keywords: ["ghost", "spooky"] },
  { emoji: "🤖", keywords: ["robot", "bot", "tech"] },
  { emoji: "💩", keywords: ["poop", "funny", "mess"] },
  { emoji: "👍", keywords: ["thumbs up", "yes", "like", "approve"] },
  { emoji: "👎", keywords: ["thumbs down", "no", "dislike"] },
  { emoji: "👌", keywords: ["ok", "perfect"] },
  { emoji: "✌️", keywords: ["peace", "victory"] },
  { emoji: "🤞", keywords: ["crossed fingers", "luck", "hope"] },
  { emoji: "🤟", keywords: ["love you", "sign"] },
  { emoji: "🤘", keywords: ["rock on", "horns"] },
  { emoji: "🤙", keywords: ["call me", "shaka"] },
  { emoji: "👋", keywords: ["wave", "hello", "bye"] },
  { emoji: "🤝", keywords: ["handshake", "agreement"] },
  { emoji: "👏", keywords: ["clap", "applause", "congrats"] },
  { emoji: "🙌", keywords: ["raised hands", "celebrate"] },
  { emoji: "🫶", keywords: ["heart hands", "love", "support"] },
  { emoji: "🙏", keywords: ["pray", "please", "thanks", "hope"] },
  { emoji: "💪", keywords: ["muscle", "strong", "strength"] },
  { emoji: "👀", keywords: ["eyes", "look", "watch"] },
  { emoji: "🧠", keywords: ["brain", "think", "smart"] },
  { emoji: "🫂", keywords: ["hug", "support", "people hugging"] },
  { emoji: "🧡", keywords: ["orange heart", "love"] },
  { emoji: "💛", keywords: ["yellow heart", "love", "friendship"] },
  { emoji: "💚", keywords: ["green heart", "love"] },
  { emoji: "💙", keywords: ["blue heart", "love"] },
  { emoji: "💜", keywords: ["purple heart", "love"] },
  { emoji: "🖤", keywords: ["black heart", "love"] },
  { emoji: "🤍", keywords: ["white heart", "love"] },
  { emoji: "🤎", keywords: ["brown heart", "love"] },
  { emoji: "🩷", keywords: ["pink heart", "love"] },
  { emoji: "🩵", keywords: ["light blue heart", "love"] },
  { emoji: "🩶", keywords: ["gray heart", "love"] },
  { emoji: "💔", keywords: ["broken heart", "sad"] },
  { emoji: "❤️‍🔥", keywords: ["heart on fire", "passion", "love"] },
  { emoji: "❤️‍🩹", keywords: ["mending heart", "healing"] },
  { emoji: "💕", keywords: ["two hearts", "love"] },
  { emoji: "❣️", keywords: ["heart exclamation", "love"] },
  { emoji: "💯", keywords: ["hundred", "perfect", "100"] },
  { emoji: "💥", keywords: ["boom", "impact"] },
  { emoji: "✨", keywords: ["sparkles", "shine", "magic"] },
  { emoji: "🐰", keywords: ["rabbit", "bunny", "animal"] },
  { emoji: "🐨", keywords: ["koala", "animal"] },
  { emoji: "🐯", keywords: ["tiger", "animal"] },
  { emoji: "🐮", keywords: ["cow", "animal"] },
  { emoji: "🐷", keywords: ["pig", "animal"] },
  { emoji: "🐔", keywords: ["chicken", "animal"] },
  { emoji: "🐧", keywords: ["penguin", "animal"] },
  { emoji: "🐦", keywords: ["bird", "animal"] },
  { emoji: "🦆", keywords: ["duck", "bird"] },
  { emoji: "🦅", keywords: ["eagle", "bird"] },
  { emoji: "🐺", keywords: ["wolf", "animal"] },
  { emoji: "🐴", keywords: ["horse", "animal"] },
  { emoji: "🐝", keywords: ["bee", "insect"] },
  { emoji: "🦋", keywords: ["butterfly", "insect"] },
  { emoji: "🐞", keywords: ["ladybug", "insect"] },
  { emoji: "🐜", keywords: ["ant", "insect"] },
  { emoji: "🕷️", keywords: ["spider", "animal"] },
  { emoji: "🐍", keywords: ["snake", "animal"] },
  { emoji: "🦎", keywords: ["lizard", "animal"] },
  { emoji: "🐙", keywords: ["octopus", "ocean"] },
  { emoji: "🦀", keywords: ["crab", "seafood"] },
  { emoji: "🐠", keywords: ["tropical fish", "fish"] },
  { emoji: "🐬", keywords: ["dolphin", "ocean"] },
  { emoji: "🐳", keywords: ["whale", "ocean"] },
  { emoji: "🦈", keywords: ["shark", "ocean"] },
  { emoji: "🌱", keywords: ["seedling", "plant", "growth"] },
  { emoji: "🍀", keywords: ["four leaf clover", "luck"] },
  { emoji: "🌵", keywords: ["cactus", "desert"] },
  { emoji: "🌴", keywords: ["palm tree", "tropical"] },
  { emoji: "🌳", keywords: ["tree", "nature"] },
  { emoji: "🍁", keywords: ["maple leaf", "fall", "autumn"] },
  { emoji: "🌹", keywords: ["rose", "flower", "love"] },
  { emoji: "🌻", keywords: ["sunflower", "flower"] },
  { emoji: "🌸", keywords: ["cherry blossom", "flower"] },
  { emoji: "💐", keywords: ["bouquet", "flowers"] },
  { emoji: "🌍", keywords: ["earth", "world", "globe"] },
  { emoji: "🌙", keywords: ["crescent moon", "night"] },
  { emoji: "⭐", keywords: ["star", "favorite"] },
  { emoji: "🌟", keywords: ["glowing star", "shine"] },
  { emoji: "⚡", keywords: ["lightning", "energy"] },
  { emoji: "☁️", keywords: ["cloud", "weather"] },
  { emoji: "☔", keywords: ["umbrella", "rain"] },
  { emoji: "💧", keywords: ["droplet", "water"] },
  { emoji: "🍌", keywords: ["banana", "fruit"] },
  { emoji: "🍇", keywords: ["grapes", "fruit"] },
  { emoji: "🍓", keywords: ["strawberry", "fruit"] },
  { emoji: "🍒", keywords: ["cherries", "fruit"] },
  { emoji: "🍑", keywords: ["peach", "fruit"] },
  { emoji: "🍍", keywords: ["pineapple", "fruit"] },
  { emoji: "🥝", keywords: ["kiwi", "fruit"] },
  { emoji: "🍅", keywords: ["tomato", "food"] },
  { emoji: "🍆", keywords: ["eggplant", "vegetable"] },
  { emoji: "🥕", keywords: ["carrot", "vegetable"] },
  { emoji: "🌽", keywords: ["corn", "vegetable"] },
  { emoji: "🍞", keywords: ["bread", "food"] },
  { emoji: "🧀", keywords: ["cheese", "food"] },
  { emoji: "🥚", keywords: ["egg", "food"] },
  { emoji: "🍳", keywords: ["fried egg", "breakfast"] },
  { emoji: "🥞", keywords: ["pancakes", "breakfast"] },
  { emoji: "🥓", keywords: ["bacon", "food"] },
  { emoji: "🌭", keywords: ["hot dog", "food"] },
  { emoji: "🍟", keywords: ["fries", "food"] },
  { emoji: "🌮", keywords: ["taco", "food"] },
  { emoji: "🌯", keywords: ["burrito", "food"] },
  { emoji: "🥗", keywords: ["salad", "healthy"] },
  { emoji: "🍝", keywords: ["spaghetti", "pasta"] },
  { emoji: "🍜", keywords: ["noodles", "ramen"] },
  { emoji: "🍲", keywords: ["soup", "stew"] },
  { emoji: "🥟", keywords: ["dumpling", "food"] },
  { emoji: "🍪", keywords: ["cookie", "dessert"] },
  { emoji: "🍫", keywords: ["chocolate", "sweet"] },
  { emoji: "🍭", keywords: ["lollipop", "candy"] },
  { emoji: "☕", keywords: ["coffee", "drink"] },
  { emoji: "🍵", keywords: ["tea", "drink"] },
  { emoji: "🥤", keywords: ["drink", "soda"] },
  { emoji: "🧋", keywords: ["bubble tea", "boba"] },
  { emoji: "🍻", keywords: ["cheers", "beer"] },
  { emoji: "🍷", keywords: ["wine", "drink"] },
  { emoji: "⚾", keywords: ["baseball", "sports"] },
  { emoji: "🎾", keywords: ["tennis", "sports"] },
  { emoji: "🏐", keywords: ["volleyball", "sports"] },
  { emoji: "🏓", keywords: ["ping pong", "sports"] },
  { emoji: "🏸", keywords: ["badminton", "sports"] },
  { emoji: "🏒", keywords: ["hockey", "sports"] },
  { emoji: "⛳", keywords: ["golf", "sports"] },
  { emoji: "🥊", keywords: ["boxing", "sports"] },
  { emoji: "🛹", keywords: ["skateboard", "sports"] },
  { emoji: "🏄", keywords: ["surfing", "sports"] },
  { emoji: "🏊", keywords: ["swimming", "sports"] },
  { emoji: "🚴", keywords: ["cycling", "bike"] },
  { emoji: "🥇", keywords: ["gold medal", "first"] },
  { emoji: "🥈", keywords: ["silver medal", "second"] },
  { emoji: "🥉", keywords: ["bronze medal", "third"] },
  { emoji: "🎨", keywords: ["art", "paint"] },
  { emoji: "🎬", keywords: ["movie", "film"] },
  { emoji: "🎤", keywords: ["microphone", "sing"] },
  { emoji: "🎧", keywords: ["headphones", "music"] },
  { emoji: "🎹", keywords: ["piano", "music"] },
  { emoji: "🥁", keywords: ["drum", "music"] },
  { emoji: "🎷", keywords: ["saxophone", "music"] },
  { emoji: "🎺", keywords: ["trumpet", "music"] },
  { emoji: "🎸", keywords: ["guitar", "music"] },
  { emoji: "🎻", keywords: ["violin", "music"] },
  { emoji: "🎲", keywords: ["dice", "game"] },
  { emoji: "♟️", keywords: ["chess", "game"] },
  { emoji: "🧩", keywords: ["puzzle", "piece"] },
  { emoji: "🚕", keywords: ["taxi", "vehicle"] },
  { emoji: "🚌", keywords: ["bus", "vehicle"] },
  { emoji: "🚑", keywords: ["ambulance", "emergency"] },
  { emoji: "🚒", keywords: ["fire truck", "emergency"] },
  { emoji: "🚚", keywords: ["truck", "delivery"] },
  { emoji: "🏍️", keywords: ["motorcycle", "vehicle"] },
  { emoji: "🚇", keywords: ["metro", "subway"] },
  { emoji: "🚆", keywords: ["train", "rail"] },
  { emoji: "🚁", keywords: ["helicopter", "flight"] },
  { emoji: "🛶", keywords: ["canoe", "boat"] },
  { emoji: "⛵", keywords: ["sailboat", "boat"] },
  { emoji: "⚓", keywords: ["anchor", "boat"] },
  { emoji: "🗺️", keywords: ["map", "travel"] },
  { emoji: "🏰", keywords: ["castle", "travel"] },
  { emoji: "🎡", keywords: ["ferris wheel", "amusement"] },
  { emoji: "🎢", keywords: ["roller coaster", "amusement"] },
  { emoji: "🏖️", keywords: ["beach", "vacation"] },
  { emoji: "🏝️", keywords: ["island", "vacation"] },
  { emoji: "⛰️", keywords: ["mountain", "nature"] },
  { emoji: "🏕️", keywords: ["camping", "outdoors"] },
  { emoji: "🏠", keywords: ["house", "home"] },
  { emoji: "🏢", keywords: ["office", "building"] },
  { emoji: "🏥", keywords: ["hospital", "building"] },
  { emoji: "🏫", keywords: ["school", "building"] },
  { emoji: "🏨", keywords: ["hotel", "travel"] },
  { emoji: "⌚", keywords: ["watch", "time"] },
  { emoji: "💻", keywords: ["laptop", "computer", "work"] },
  { emoji: "⌨️", keywords: ["keyboard", "computer"] },
  { emoji: "🖨️", keywords: ["printer", "office"] },
  { emoji: "🖱️", keywords: ["mouse", "computer"] },
  { emoji: "💾", keywords: ["save", "floppy disk"] },
  { emoji: "📷", keywords: ["camera", "photo"] },
  { emoji: "📹", keywords: ["video camera", "record"] },
  { emoji: "📞", keywords: ["phone", "call"] },
  { emoji: "📺", keywords: ["television", "tv"] },
  { emoji: "⏰", keywords: ["alarm", "clock"] },
  { emoji: "🔋", keywords: ["battery", "power"] },
  { emoji: "🔌", keywords: ["plug", "power"] },
  { emoji: "🔦", keywords: ["flashlight", "light"] },
  { emoji: "💵", keywords: ["dollar", "money"] },
  { emoji: "💳", keywords: ["credit card", "payment"] },
  { emoji: "💎", keywords: ["gem", "diamond"] },
  { emoji: "⚙️", keywords: ["gear", "settings"] },
  { emoji: "🛠️", keywords: ["tools", "hammer wrench"] },
  { emoji: "🛡️", keywords: ["shield", "security"] },
  { emoji: "🩹", keywords: ["bandage", "medical"] },
  { emoji: "🩺", keywords: ["stethoscope", "medical"] },
  { emoji: "💊", keywords: ["pill", "medicine"] },
  { emoji: "🧬", keywords: ["dna", "science"] },
  { emoji: "🔬", keywords: ["microscope", "science"] },
  { emoji: "🧹", keywords: ["broom", "clean"] },
  { emoji: "🧼", keywords: ["soap", "clean"] },
  { emoji: "🪥", keywords: ["toothbrush", "dental"] },
  { emoji: "🚪", keywords: ["door", "entry"] },
  { emoji: "🛏️", keywords: ["bed", "sleep"] },
  { emoji: "🪞", keywords: ["mirror", "reflection"] },
  { emoji: "🛍️", keywords: ["shopping bags", "shopping"] },
  { emoji: "🛒", keywords: ["shopping cart", "shopping"] },
  { emoji: "🪄", keywords: ["magic wand", "magic"] },
  { emoji: "✉️", keywords: ["envelope", "mail"] },
  { emoji: "📧", keywords: ["email", "mail"] },
  { emoji: "📥", keywords: ["inbox", "mail"] },
  { emoji: "📤", keywords: ["outbox", "mail"] },
  { emoji: "📦", keywords: ["package", "delivery"] },
  { emoji: "📊", keywords: ["bar chart", "analytics"] },
  { emoji: "📈", keywords: ["chart up", "growth", "analytics"] },
  { emoji: "📉", keywords: ["chart down", "decline", "analytics"] },
  { emoji: "📅", keywords: ["calendar", "date"] },
  { emoji: "📋", keywords: ["clipboard", "list"] },
  { emoji: "📁", keywords: ["folder", "files"] },
  { emoji: "📚", keywords: ["books", "library"] },
  { emoji: "🔗", keywords: ["link", "chain"] },
  { emoji: "📎", keywords: ["paperclip", "attachment"] },
  { emoji: "📌", keywords: ["pushpin", "pin"] },
  { emoji: "📍", keywords: ["location pin", "map"] },
  { emoji: "✂️", keywords: ["scissors", "cut"] },
  { emoji: "📝", keywords: ["memo", "note", "write"] },
  { emoji: "✏️", keywords: ["pencil", "write"] },
  { emoji: "🔍", keywords: ["search", "magnifying glass"] },
  { emoji: "🔒", keywords: ["lock", "security"] },
  { emoji: "🔓", keywords: ["unlock", "security"] },
  { emoji: "✅", keywords: ["check", "done", "complete", "yes"] },
  { emoji: "✔️", keywords: ["check mark", "yes"] },
  { emoji: "❌", keywords: ["x", "wrong", "no"] },
  { emoji: "➕", keywords: ["plus", "add"] },
  { emoji: "➖", keywords: ["minus", "remove"] },
  { emoji: "♾️", keywords: ["infinity", "forever"] },
  { emoji: "❓", keywords: ["question", "help"] },
  { emoji: "❗", keywords: ["exclamation", "alert"] },
  { emoji: "⚠️", keywords: ["warning", "alert"] },
  { emoji: "ℹ️", keywords: ["information", "info"] },
  { emoji: "♻️", keywords: ["recycle", "green"] },
  { emoji: "🔴", keywords: ["red circle", "status"] },
  { emoji: "🟠", keywords: ["orange circle", "status"] },
  { emoji: "🟡", keywords: ["yellow circle", "status"] },
  { emoji: "🟢", keywords: ["green circle", "status"] },
  { emoji: "🔵", keywords: ["blue circle", "status"] },
  { emoji: "🟣", keywords: ["purple circle", "status"] },
  { emoji: "⚫", keywords: ["black circle", "status"] },
  { emoji: "⚪", keywords: ["white circle", "status"] },
  { emoji: "▶️", keywords: ["play", "right"] },
  { emoji: "⏸️", keywords: ["pause"] },
  { emoji: "⏹️", keywords: ["stop"] },
  { emoji: "🔁", keywords: ["repeat"] },
  { emoji: "➡️", keywords: ["right arrow"] },
  { emoji: "⬅️", keywords: ["left arrow"] },
  { emoji: "⬆️", keywords: ["up arrow"] },
  { emoji: "⬇️", keywords: ["down arrow"] },
  { emoji: "🔄", keywords: ["refresh", "reload"] },
  { emoji: "🏳️‍🌈", keywords: ["rainbow flag", "pride"] },
  { emoji: "🏳️‍⚧️", keywords: ["transgender flag", "pride"] },
  { emoji: "🇲🇽", keywords: ["mexico", "flag"] },
  { emoji: "🇧🇷", keywords: ["brazil", "flag"] },
  { emoji: "🇫🇷", keywords: ["france", "flag"] },
  { emoji: "🇩🇪", keywords: ["germany", "flag"] },
  { emoji: "🇪🇸", keywords: ["spain", "flag"] },
  { emoji: "🇮🇹", keywords: ["italy", "flag"] },
  { emoji: "🇮🇳", keywords: ["india", "flag"] },
  { emoji: "🇨🇳", keywords: ["china", "flag"] },
  { emoji: "🇯🇵", keywords: ["japan", "flag"] },
  { emoji: "🇰🇷", keywords: ["south korea", "flag"] },
  { emoji: "🇵🇭", keywords: ["philippines", "flag"] },
  { emoji: "🇳🇿", keywords: ["new zealand", "flag"] },
];
